import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Play, ArrowLeft, Clock, Video as VideoIcon, Plus, Loader2 } from 'lucide-react';
import TeamGate from '../components/TeamGate';
import { playlistsAPI, videosAPI } from '../services/api';
import SimpleVideoPlayer from '../components/SimpleVideoPlayer';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { BACKEND_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const PlaylistDetail = () => {
    const { playlistId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const [playlist, setPlaylist] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);
    const [loading, setLoading] = useState(true);

    const [addVideoModalOpen, setAddVideoModalOpen] = useState(false);
    const [allVideos, setAllVideos] = useState([]);
    const [addingVideoId, setAddingVideoId] = useState(null);

    const canEdit = user?.role === 'coach' || user?.role === 'admin';

    useEffect(() => {
        const fetchPlaylist = async () => {
            try {
                const response = await playlistsAPI.get(playlistId);
                setPlaylist(response.data);
                if (response.data.videos && response.data.videos.length > 0) {
                    setActiveVideo(response.data.videos[0]);
                }
            } catch (error) {
                console.error('Failed to fetch playlist', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPlaylist();
    }, [playlistId]);

    const formatDuration = (seconds) => {
        if (!seconds) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleOpenAddVideo = async () => {
        setAddVideoModalOpen(true);
        try {
            const response = await videosAPI.list();
            setAllVideos(response.data);
        } catch (error) {
            console.error('Failed to fetch videos', error);
        }
    };

    const confirmAddVideo = async (videoId) => {
        setAddingVideoId(videoId);
        try {
            await playlistsAPI.addVideo(playlistId, videoId);
            // Refresh playlist
            const response = await playlistsAPI.get(playlistId);
            setPlaylist(response.data);
            setAddVideoModalOpen(false);
            if (!activeVideo && response.data.videos.length > 0) {
                setActiveVideo(response.data.videos[0]);
            }
        } catch (error) {
            console.error('Failed to add video', error);
        } finally {
            setAddingVideoId(null);
        }
    };

    if (loading) {
        return (
            <TeamGate title="Playlist">
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
            </TeamGate>
        );
    }

    if (!playlist) {
        return (
            <TeamGate title="Playlist">
                <div className="text-center py-20 text-gray-500">Playlist not found</div>
            </TeamGate>
        );
    }

    // Function to get the correct video stream URL
    const getVideoUrl = (video) => {
        if (!video) return '';
        let url = video.url || '';
        if (url && url.startsWith('/api')) {
            url = `${BACKEND_URL}${url}`;
        }
        if (url && url.includes(BACKEND_URL) && !url.includes('token=')) {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}token=${localStorage.getItem('token')}`;
        }
        return url;
    };

    const getThumbnailUrl = (video) => {
        if (!video || !video.thumbnail) return '/placeholder-video.png';
        let url = video.thumbnail;
        if (url.startsWith('/api')) {
            url = `${BACKEND_URL}${url}`;
        }
        if (url && url.includes(BACKEND_URL) && !url.includes('token=')) {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}token=${localStorage.getItem('token')}`;
        }
        return url;
    };

    return (
        <TeamGate title={playlist.name}>
            <div className="p-4 lg:p-8 pt-[80px] lg:pt-8 min-h-screen bg-[#0a0a0b] flex flex-col">
                <Button 
                    variant="ghost" 
                    onClick={() => navigate('/playlists')}
                    className="text-gray-400 hover:text-white mb-6 p-0 h-auto justify-start self-start"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Playlists
                </Button>

                <div className="grid lg:grid-cols-3 gap-6 flex-1">
                    {/* Main Video View */}
                    <div className="lg:col-span-2 flex flex-col h-full rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10 bg-[#141415]">
                        <div className="relative aspect-video w-full bg-black">
                            {activeVideo ? (
                                <SimpleVideoPlayer 
                                    src={getVideoUrl(activeVideo)} 
                                    poster={getThumbnailUrl(activeVideo)}
                                    autoPlay={true}
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                                    <VideoIcon className="w-16 h-16 mb-4 opacity-20" />
                                    <p>No videos in playlist</p>
                                </div>
                            )}
                        </div>
                        <div className="p-6 pb-8">
                            <h1 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight">
                                {activeVideo ? activeVideo.title : playlist.name}
                            </h1>
                            <p className="text-gray-400 font-medium tracking-wide">
                                {playlist.description || "No description provided."}
                            </p>
                        </div>
                    </div>

                    {/* Playlist Sidebar */}
                    <div className="lg:col-span-1 h-[60vh] lg:h-auto flex flex-col bg-[#141415] rounded-[1.5rem] border border-white/5 overflow-hidden shadow-xl">
                        <div className="p-6 bg-gradient-to-b from-white/5 to-transparent border-b border-white/5">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <h2 className="text-xl font-black text-white">{playlist.name}</h2>
                                    <div className="flex gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">
                                        <span className="flex items-center gap-1">
                                            <VideoIcon className="w-4 h-4" /> {playlist.videos.length} clips
                                        </span>
                                    </div>
                                </div>
                                {canEdit && (
                                    <button 
                                        onClick={handleOpenAddVideo}
                                        className="bg-snapp-blue/20 hover:bg-snapp-blue/30 text-snapp-blue p-2 rounded-xl transition-colors shrink-0"
                                        title="Video Ekle"
                                    >
                                        <Plus className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto w-full custom-scrollbar content-start p-3 gap-3 flex flex-col">
                            {playlist.videos.map((video, idx) => {
                                const isActive = activeVideo?.id === video.id;
                                return (
                                    <button 
                                        key={idx}
                                        onClick={() => setActiveVideo(video)}
                                        className={cn(
                                            "flex w-full overflow-hidden text-left bg-[#1a1b1e] rounded-xl transition-all duration-300 relative group items-stretch",
                                            isActive 
                                                ? "ring-2 ring-snapp-purple shadow-[0_0_20px_-5px_rgba(147,51,234,0.3)] bg-gradient-to-r from-snapp-purple/10 to-transparent" 
                                                : "hover:bg-[#232429] ring-1 ring-white/5 hover:ring-white/10"
                                        )}
                                    >
                                        <div className="relative w-32 md:w-40 shrink-0 bg-black overflow-hidden flex items-center justify-center">
                                            <img 
                                                src={getThumbnailUrl(video)}
                                                alt={video.title} 
                                                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                            />
                                            {isActive && (
                                                <div className="absolute inset-0 bg-snapp-purple/40 flex items-center justify-center backdrop-blur-[2px]">
                                                    <Play className="w-6 h-6 text-white fill-white animate-pulse" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3 md:p-4 flex flex-col justify-center flex-1 min-w-0">
                                            <h3 className={cn(
                                                "font-bold text-sm md:text-base leading-tight decoration-slice mb-1.5 truncate text-white"
                                            )}>
                                                {video.title}
                                            </h3>
                                            <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-widest">
                                                <span className="flex items-center gap-1 bg-black/40 px-1.5 py-0.5 rounded">
                                                    <Clock className="w-3 h-3" /> {formatDuration(video.duration)}
                                                </span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}

                            {playlist.videos.length === 0 && (
                                <div className="text-center py-10 text-gray-500 text-sm">
                                    No videos added to this playlist yet.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Video Dialog */}
            <Dialog open={addVideoModalOpen} onOpenChange={setAddVideoModalOpen}>
                <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-md" aria-describedby="add-video-desc">
                    <DialogHeader>
                        <DialogTitle>Videolardan Seç</DialogTitle>
                        <DialogDescription id="add-video-desc" className="text-gray-400 text-sm">
                            Bu playlist'e eklemek istediğiniz videoları seçin.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {allVideos.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500 text-sm mb-4">Henüz takıma yüklenmiş video yok.</p>
                                <Button onClick={() => navigate('/videos')} className="bg-snapp-blue text-white w-full">
                                    Videolara Git ve Yükle
                                </Button>
                            </div>
                        ) : (
                            allVideos.map(video => {
                                const isAlreadyInPlaylist = playlist.videos.some(pv => pv.id === video.id);
                                return (
                                    <button
                                        key={video.id}
                                        onClick={() => !isAlreadyInPlaylist && confirmAddVideo(video.id)}
                                        disabled={addingVideoId === video.id || isAlreadyInPlaylist}
                                        className={cn(
                                            "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left group",
                                            isAlreadyInPlaylist 
                                                ? "bg-white/5 border-white/5 opacity-50 cursor-not-allowed" 
                                                : "bg-[#0a0a0b] hover:bg-white/5 border-white/5"
                                        )}
                                    >
                                        <div className="relative w-16 h-12 shrink-0 bg-black rounded overflow-hidden flex items-center justify-center">
                                            <img 
                                                src={getThumbnailUrl(video)}
                                                alt={video.title} 
                                                className="w-full h-full object-cover opacity-80"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-bold text-white truncate">{video.title || "İsimsiz Video"}</h4>
                                            <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                                                {formatDuration(video.duration_seconds)}
                                                {isAlreadyInPlaylist && " • Eklendi"}
                                            </p>
                                        </div>
                                        {addingVideoId === video.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin text-snapp-blue" />
                                        ) : !isAlreadyInPlaylist && (
                                            <Plus className="w-4 h-4 text-gray-500 group-hover:text-snapp-blue transition-colors" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                        
                        {allVideos.length > 0 && (
                            <div className="pt-4 mt-2 border-t border-white/10">
                                <Button 
                                    onClick={() => navigate('/videos')} 
                                    variant="outline" 
                                    className="w-full border-white/10 text-gray-400 hover:text-white hover:bg-white/5 bg-transparent"
                                >
                                    Yeni Video Yüklemek İçin Videolara Git
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </TeamGate>
    );
};

export default PlaylistDetail;
